import { useEffect, useState } from 'react';
import { Drawer, Head, SaveView, sortOf, useAdmin, useDrawerRoute, useFilters } from '../ui/Shell';
import { Block, Cell, DateRange, Grid, Kv, ListMore, Pill, Table, type Row } from '../ui/Block';
import { fmtDate, get, type Env } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';
import { countOf, pagePath, qs, useDebounced, usePagedList, type Paged } from '../lib/list';
import { downloadText } from './Analytics';

interface Conversation {
  id: string; userId: string; principalId: string; email: string | null; name: string | null;
  startedAt: string; lastAt: string; turns: number; failures: number; firstText: string;
  provider: string; model: string; inputTokens: number; outputTokens: number; costMicro?: number;
}
interface Message { id: string; seq: number; role: 'user' | 'assistant'; text: string; go: string | null; at: string; callMs: number | null; inputTokens: number | null; outputTokens: number | null; costMicro: number | null; error: string | null }
interface Detail extends Conversation { messages: Message[] }

const csvCell = (v: unknown) => { const s = v == null ? '' : typeof v === 'string' ? v : JSON.stringify(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
const usd = (micro: number | null | undefined) => micro == null ? '—' : `$${(micro / 1e6).toFixed(micro < 1_000_000 ? 4 : 2)}`;
const who = (c: { email: string | null; name: string | null; userId: string }) => c.email || c.name || `${c.userId.slice(0, 8)}…`;

function useSearch(value: string, write: (v: string) => void): [string, (v: string) => void] {
  const [q, setQ] = useState(value);
  const dq = useDebounced(q.trim(), 300);
  useEffect(() => { if (dq !== value) write(dq); }, [dq]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setQ((c) => (c.trim() === value ? c : value)); }, [value]);
  return [q, setQ];
}

type List = Paged & { conversations: Conversation[]; spentTodayMicro?: number; dailyBudgetMicro?: number };
const EXPORT_MAX = 10_000;

function ConversationDrawer({ env, id, paused }: { env: Env; id: string; paused: boolean }) {
  const d = useData<Detail>(env, `/admin/ask/${id}`, 30, paused);
  const c = d.data;
  return (
    <Drawer eyebrow="ASSISTANT CONVERSATION" title={c ? who(c) : '…'} sub={c ? `${fmtDate(c.startedAt)} · ${c.turns} question${c.turns === 1 ? '' : 's'} · ${c.provider} ${c.model}` : undefined} wide>
      {!c && d.loading && <div style={{ color: 'var(--ink-45)', fontSize: 12 }}>Loading…</div>}
      {describeErr(d.error) && <div style={{ color: 'var(--conf-low)', fontSize: 12 }}>{describeErr(d.error)!.title}</div>}
      {c && (
        <>
          <Kv rows={[
            { k: 'User', v: { t: who(c), sub: c.userId, mono: true } },
            { k: 'Started', v: { t: fmtDate(c.startedAt) } },
            { k: 'Last message', v: { t: fmtDate(c.lastAt) } },
            { k: 'Cost', v: { t: usd(c.costMicro), sub: `${c.inputTokens.toLocaleString()} in · ${c.outputTokens.toLocaleString()} out tokens`, mono: true } },
          ]} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 14 }}>
            {c.messages.map((m) => (
              <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{ maxWidth: '86%', padding: '8px 11px', borderRadius: 10, background: m.role === 'user' ? 'var(--brand-10, var(--fill-08))' : 'var(--fill-05)', color: m.error ? 'var(--conf-low)' : 'var(--ink-96)', fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {m.error ? `No answer: ${m.error}` : m.text}
                  {m.go && <div style={{ marginTop: 6 }}><Pill tone="info">→ {m.go}</Pill></div>}
                </div>
                <div style={{ font: '400 10.5px var(--vs-font-mono)', color: 'var(--ink-45)', marginTop: 3 }}>
                  {m.role === 'user' ? 'user' : 'assistant'} · {fmtDate(m.at)}{m.callMs != null ? ` · ${(m.callMs / 1000).toFixed(1)} s` : ''}{m.inputTokens != null ? ` · ${m.inputTokens}/${m.outputTokens ?? 0} tok` : ''}{m.costMicro != null ? ` · ${usd(m.costMicro)}` : ''}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </Drawer>
  );
}

export function Ask() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const [f, set] = useFilters({ q: '', user: '', from: '', to: '', failed: '', sort: '', dir: '' });
  const s = sortOf(f, set, { key: 'last', dir: 'desc' });
  const [q, setQ] = useSearch(f.q, (v) => set({ q: v }));
  const [exporting, setExporting] = useState(false);
  const failed = f.failed === '1';

  const path = `/admin/ask${qs({ q: f.q, user: f.user, from: f.from, to: f.to, failed, sort: f.sort, dir: f.sort ? s.sort.dir : '' })}`;
  const l = usePagedList<Conversation, List>(env, path, 'conversations', 30, paused);
  const drawer = (id: string) => <ConversationDrawer env={env} id={id} paused={paused} />;
  useDrawerRoute('ask', drawer, []);
  const list = l.rows;
  const listCost = list.reduce((n, c) => n + (c.costMicro ?? 0), 0);
  const spent = l.data?.spentTodayMicro ?? 0;
  const budget = l.data?.dailyBudgetMicro ?? 0;

  const csv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      let all: Conversation[] = [];
      for (let off: number | null = 0; off !== null && all.length < EXPORT_MAX;) {
        const d: List = await get<List>(env, pagePath(path, off, 200));
        all = all.concat(d.conversations ?? []);
        off = d.next ?? null;
      }
      const head = ['startedAt', 'lastAt', 'user', 'userId', 'turns', 'failures', 'firstText', 'provider', 'model', 'inputTokens', 'outputTokens', 'costUsd', 'id'];
      const lines = [head.join(','), ...all.map((c) => [c.startedAt, c.lastAt, who(c), c.userId, c.turns, c.failures, c.firstText, c.provider, c.model, c.inputTokens, c.outputTokens, c.costMicro == null ? '' : (c.costMicro / 1e6).toFixed(6), c.id].map(csvCell).join(','))];
      downloadText(`assistant-conversations-${new Date().toISOString().slice(0, 10)}.csv`, lines.join('\n'));
      ctx.toast(`Exported ${all.length.toLocaleString()} conversations`, `Every one matching the filters (${EXPORT_MAX.toLocaleString()} at most)`, { logged: false });
    } catch (e) {
      ctx.toast('Not exported', e instanceof Error ? e.message : String(e), { logged: false });
    } finally {
      setExporting(false);
    }
  };

  const rows: Row[] = list.map((c) => ({
    key: c.id,
    onClick: () => ctx.openDrawer(`ask:${c.id}`, drawer(c.id)),
    cells: [
      { t: fmtDate(c.lastAt), sub: c.startedAt !== c.lastAt ? `from ${fmtDate(c.startedAt)}` : undefined, mono: true },
      { t: who(c), sub: c.email && c.name ? c.name : undefined },
      { t: c.firstText || '—', fg: c.firstText ? undefined : 'var(--ink-40)' },
      { t: String(c.turns), mono: true, pill: c.failures > 0 ? { label: `${c.failures} FAILED`, tone: 'bad' } : undefined },
      { t: usd(c.costMicro), sub: `${c.inputTokens.toLocaleString()} / ${c.outputTokens.toLocaleString()} tok · ${c.model}`, mono: true },
    ],
  }));

  return (
    <>
      <Head title="Assistant" sub="Every conversation with the Dashboard's assistant: the questions, the answers and where they pointed. Click a row to read it." />
      {l.data?.spentTodayMicro != null && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, margin: '0 0 12px', font: '400 12px var(--vs-font-mono)', color: 'var(--ink-62)' }}>
          <span>Today (UTC): <b style={{ color: budget > 0 && spent >= budget ? 'var(--conf-low)' : 'var(--ink-96)' }}>{usd(spent)}</b>{budget > 0 ? ` of ${usd(budget)} daily budget` : ' · no daily budget'}</span>
          <span>Loaded: <b style={{ color: 'var(--ink-96)' }}>{usd(listCost)}</b> across {list.length.toLocaleString()} conversation{list.length === 1 ? '' : 's'}{l.total !== null && l.rows.length < l.total ? ` (${l.total.toLocaleString()} match)` : ''}</span>
        </div>
      )}
      <Grid>
        <Cell span={12}>
          <Block title="Conversations" count={countOf(l)} asOf={hhmmss(l.at)} stale={l.ageS > 60} csv={csv} loading={l.loading && !l.data} error={describeErr(l.error) ? { ...describeErr(l.error)!, retry: l.reload } : null}
            search={{ value: q, placeholder: 'A word in any message, or who asked', onChange: setQ }}
            chips={[
              { label: 'With a failed answer', on: failed, onClick: () => set({ failed: failed ? '' : '1' }) },
              ...(f.user ? [{ label: `User ${f.user.slice(0, 8)}… ✕`, on: true, onClick: () => set({ user: '' }) }] : []),
            ]}
            extra={<><DateRange label="Last message" from={f.from} to={f.to} onChange={(from, to) => set({ from, to })} /><SaveView /></>}
            empty={l.data && list.length === 0 ? 'No conversations match.' : undefined}
            foot={<ListMore list={l} noun="conversations" />}>
            <Table mobile={mobile} minWidth={900} rows={rows} sort={s.sort} onSort={s.onSort}
              cols={[{ label: 'LAST MESSAGE', width: '150px', sort: 'last' }, { label: 'WHO', width: 'minmax(160px,1fr)', sort: 'who', sortAsc: true }, { label: 'FIRST QUESTION', width: 'minmax(260px,2.2fr)', sort: 'question', sortAsc: true }, { label: 'TURNS', width: '120px', sort: 'turns' }, { label: 'COST', width: 'minmax(170px,1fr)', sort: 'cost' }]} />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
